import { motion } from "framer-motion";
import { MessageCircle, Phone, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import LogoAssembly from "@/components/LogoAssembly";

const WA = "https://wa.me/393534520559?text=" + encodeURIComponent(
  "Ciao Davide, vorrei vendere la mia vettura. Ti scrivo i dettagli:"
);

const CONTENT = {
  it: {
    eyebrow: "Vendi la tua",
    title1: "Affida la tua vettura",
    title2: "a chi la sa valorizzare.",
    intro:
      "Che sia una supercar moderna o una classica da collezione, mi occupo di trovare l'acquirente giusto, al giusto valore. Un unico interlocutore, dal primo messaggio alla firma.",
    ctaWa: "Scrivimi su WhatsApp",
    ctaPhone: "Chiama Davide",
    promisesTitle: "",
    promises: [
      "Valutazione gratuita e senza impegno",
      "Rete di clienti selezionati in tutta Italia",
      "Gestione completa di pratiche e passaggio",
      "Massima riservatezza e trasparenza",
    ],
    howTitle: "Come funziona",
    steps: [
      { n: "01", title: "Raccontami la tua vettura", body: "Scrivimi su WhatsApp con marca, modello, anno, chilometri e qualche foto. In poche ore ti dico se è nelle mie corde e come posso aiutarti." },
      { n: "02", title: "Valutazione trasparente", body: "Analizzo mercato, condizioni e documentazione e ti propongo la strategia migliore: acquisto diretto, conto vendita o ricerca mirata dell'acquirente giusto." },
      { n: "03", title: "Vendita senza pensieri", body: "Gestisco foto, annuncio, trattativa e passaggio di proprietà. Tu segui tutto in modo diretto, senza intermediari e senza sorprese." },
    ],
    finalTitle: "Pronto a vendere?",
    finalSub: "Bastano due righe su WhatsApp per iniziare.",
    finalCta: "Contattami ora",
  },
  en: {
    eyebrow: "Sell yours",
    title1: "Entrust your car",
    title2: "to someone who knows its worth.",
    intro:
      "Whether it's a modern supercar or a collectible classic, I find the right buyer at the right value. A single point of contact, from the first message to the signature.",
    ctaWa: "Message me on WhatsApp",
    ctaPhone: "Call Davide",
    promisesTitle: "",
    promises: [
      "Free, no-obligation valuation",
      "A network of selected clients across Italy",
      "Full handling of paperwork and transfer",
      "Utmost discretion and transparency",
    ],
    howTitle: "How it works",
    steps: [
      { n: "01", title: "Tell me about your car", body: "Message me on WhatsApp with make, model, year, mileage and a few photos. Within a few hours I'll tell you if it's a fit and how I can help." },
      { n: "02", title: "Transparent valuation", body: "I analyse the market, condition and documentation and propose the best strategy: direct purchase, consignment or a targeted search for the right buyer." },
      { n: "03", title: "Hassle-free sale", body: "I handle photos, listing, negotiation and ownership transfer. You follow everything directly, with no middlemen and no surprises." },
    ],
    finalTitle: "Ready to sell?",
    finalSub: "Just a couple of lines on WhatsApp to get started.",
    finalCta: "Contact me now",
  },
};

export default function VendiPage() {
  const { lang } = useLang();
  const t = CONTENT[lang];

  return (
    <div className="relative overflow-hidden pt-40 pb-32 px-6 sm:px-10 lg:px-14 max-w-[1600px] mx-auto" data-testid="vendi-page">
      {/* Stesso logo animato della sezione vetture, spostato di lato e più tenue */}
      <LogoAssembly className="opacity-[0.06] translate-x-[32%] sm:translate-x-[38%] translate-y-[6%]" />

      <div className="relative z-10">
      <div className="flex items-center gap-4 mb-8">
        <span className="w-10 h-px bg-white/40" />
        <span className="label-eyebrow text-white/70">{t.eyebrow}</span>
      </div>

      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        className="font-serif text-5xl sm:text-6xl lg:text-8xl font-light leading-[0.95] tracking-tighter text-white max-w-5xl"
      >
        {t.title1}<br />
        <span className="italic text-neutral-500">{t.title2}</span>
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.15 }}
        className="mt-10 text-neutral-400 text-lg sm:text-xl max-w-2xl leading-relaxed font-light"
      >
        {t.intro}
      </motion.p>

      <div className="mt-10 flex flex-wrap gap-4">
        <a href={WA} target="_blank" rel="noopener noreferrer" className="btn-magnetic btn-solid" data-testid="vendi-cta-whatsapp">
          <MessageCircle size={16} /> {t.ctaWa}
        </a>
        <a href="tel:+393534520559" className="btn-magnetic whitespace-nowrap" data-testid="vendi-cta-phone">
          <Phone size={14} /> {t.ctaPhone} <span className="tabular-nums font-medium">+39 353 452 0559</span>
        </a>
      </div>

      </div>
    </div>
  );
}
